import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  UserCheck,
  Link,
  Users,
  GripVertical,
} from "lucide-react";
import {
  approvalRolesApi,
  rolesApi,
  ApprovalRole,
  Role,
  ChecklistItem,
} from "@/api/client";

export default function ApprovalRolesSettings() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<ApprovalRole | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    display_order: 0,
    is_active: true,
    linked_user_role_ids: [] as string[],
  });

  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [newItem, setNewItem] = useState({
    text: "",
    required: true,
    description: "",
  });

  const { data: approvalRolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["approval-roles", { include_linked: true }],
    queryFn: () => approvalRolesApi.list(),
  });

  const { data: userRolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesApi.list(),
  });

  const approvalRoles = approvalRolesData?.roles || [];
  const userRoles = userRolesData?.roles || [];

  // Fetch linked roles for each approval role
  const { data: linkedRolesMap } = useQuery({
    queryKey: ["approval-roles-linked"],
    queryFn: async () => {
      const map: Record<string, Role[]> = {};
      for (const role of approvalRoles) {
        try {
          const fullRole = await approvalRolesApi.get(role.id);
          map[role.id] = fullRole.linked_user_roles || [];
        } catch {
          map[role.id] = [];
        }
      }
      return map;
    },
    enabled: approvalRoles.length > 0,
  });

  const createMutation = useMutation({
    mutationFn: approvalRolesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval-roles"] });
      handleCancel();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof approvalRolesApi.update>[1]) =>
      approvalRolesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval-roles"] });
      queryClient.invalidateQueries({ queryKey: ["approval-roles-linked"] });
      handleCancel();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: approvalRolesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["approval-roles"] });
    },
  });

  const handleCancel = () => {
    setShowForm(false);
    setEditingRole(null);
    setFormData({
      name: "",
      slug: "",
      description: "",
      display_order: 0,
      is_active: true,
      linked_user_role_ids: [],
    });
    setChecklist([]);
    setNewItem({ text: "", required: true, description: "" });
  };

  const handleEdit = async (role: ApprovalRole) => {
    // Fetch full role with linked user roles
    try {
      const fullRole = await approvalRolesApi.get(role.id);
      setEditingRole(fullRole);
      setFormData({
        name: fullRole.name,
        slug: fullRole.slug,
        description: fullRole.description || "",
        display_order: fullRole.display_order,
        is_active: fullRole.is_active,
        linked_user_role_ids:
          fullRole.linked_user_roles?.map((r) => r.id) || [],
      });

      // Parse checklist from JSON
      let parsedChecklist: ChecklistItem[] = [];
      if (fullRole.checklist) {
        if (typeof fullRole.checklist === "string") {
          parsedChecklist = JSON.parse(fullRole.checklist);
        } else {
          parsedChecklist = fullRole.checklist as ChecklistItem[];
        }
      }
      setChecklist(parsedChecklist);
      setShowForm(true);
    } catch (err) {
      console.error("Failed to fetch role:", err);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      name: formData.name,
      slug: formData.slug || undefined,
      description: formData.description || undefined,
      checklist: checklist,
      display_order: formData.display_order,
      is_active: formData.is_active,
      linked_user_role_ids: formData.linked_user_role_ids,
    };

    if (editingRole) {
      updateMutation.mutate({
        id: editingRole.id,
        ...payload,
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (role: ApprovalRole) => {
    if (
      confirm(
        `Are you sure you want to delete the "${role.name}" approval role? This action cannot be undone.`,
      )
    ) {
      deleteMutation.mutate(role.id);
    }
  };

  const toggleActive = (role: ApprovalRole) => {
    updateMutation.mutate({
      id: role.id,
      name: role.name,
      checklist: role.checklist as ChecklistItem[],
      is_active: !role.is_active,
    });
  };

  const toggleLinkedRole = (roleId: string) => {
    setFormData((prev) => ({
      ...prev,
      linked_user_role_ids: prev.linked_user_role_ids.includes(roleId)
        ? prev.linked_user_role_ids.filter((id) => id !== roleId)
        : [...prev.linked_user_role_ids, roleId],
    }));
  };

  const addChecklistItem = () => {
    if (!newItem.text.trim()) return;
    const item: ChecklistItem = {
      id: `item-${Date.now()}`,
      text: newItem.text.trim(),
      required: newItem.required,
      description: newItem.description.trim() || undefined,
    };
    setChecklist([...checklist, item]);
    setNewItem({ text: "", required: true, description: "" });
  };

  const removeChecklistItem = (itemId: string) => {
    setChecklist(checklist.filter((item) => item.id !== itemId));
  };

  const toggleItemRequired = (itemId: string) => {
    setChecklist(
      checklist.map((item) =>
        item.id === itemId ? { ...item, required: !item.required } : item,
      ),
    );
  };

  if (rolesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Approval Roles
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Define approval workflow roles and link them to user roles for
            auto-suggesting approvers
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Approval Role
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="card mb-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="input"
                    placeholder="e.g., QA Lead, Security Review"
                    required
                  />
                </div>
                <div>
                  <label className="label">Slug (optional)</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    className="input"
                    placeholder="Auto-generated if empty"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="label">Description (optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="input"
                  rows={2}
                  placeholder="Brief description of this approval role"
                />
              </div>
            </div>

            {/* Linked User Roles */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-1">
                Linked User Roles
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Select user roles that can fulfill this approval. Users with
                these roles who are marked as approvers will be auto-suggested.
              </p>
              {userRoles.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {userRoles
                    .filter((r) => r.is_active)
                    .map((role) => (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => toggleLinkedRole(role.id)}
                        className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                          formData.linked_user_role_ids.includes(role.id)
                            ? "bg-blue-50 border-blue-300 text-blue-800"
                            : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <Link className="h-3.5 w-3.5" />
                        {role.name}
                      </button>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
                  No user roles defined. Create user roles first to link them to
                  approval roles.
                </p>
              )}
            </div>

            {/* Checklist */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-1">
                Approval Checklist
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Define checklist items that approvers must complete before
                approving.
              </p>

              {/* Existing items */}
              {checklist.length > 0 && (
                <div className="space-y-2 mb-4">
                  {checklist.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-white border rounded-lg"
                    >
                      <GripVertical className="h-4 w-4 text-gray-300" />
                      <div className="flex-1">
                        <span className="text-sm text-gray-900">
                          {item.text}
                        </span>
                        {item.description && (
                          <p className="text-xs text-gray-500">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleItemRequired(item.id)}
                        className={`px-2 py-1 text-xs rounded ${
                          item.required
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {item.required ? "Required" : "Optional"}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeChecklistItem(item.id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new item */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newItem.text}
                  onChange={(e) =>
                    setNewItem({ ...newItem, text: e.target.value })
                  }
                  className="input flex-1"
                  placeholder="Checklist item text"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addChecklistItem();
                    }
                  }}
                />
                <label className="flex items-center gap-2 px-3">
                  <input
                    type="checkbox"
                    checked={newItem.required}
                    onChange={(e) =>
                      setNewItem({ ...newItem, required: e.target.checked })
                    }
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-600">Required</span>
                </label>
                <button
                  type="button"
                  onClick={addChecklistItem}
                  disabled={!newItem.text.trim()}
                  className="btn btn-secondary"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Display Order & Active */}
            <div className="border-t pt-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        display_order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="input"
                    min={0}
                  />
                </div>
                {editingRole && (
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_active: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="btn btn-primary"
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editingRole ? (
                  "Update Approval Role"
                ) : (
                  "Create Approval Role"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Approval Roles List */}
      <div className="space-y-3">
        {approvalRoles.map((role) => {
          const linkedRoles = linkedRolesMap?.[role.id] || [];
          return (
            <div
              key={role.id}
              className={`card ${!role.is_active ? "bg-gray-50" : ""}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <UserCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {role.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        ({role.slug})
                      </span>
                      {!role.is_active && (
                        <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    {role.description && (
                      <p className="text-sm text-gray-500 mt-0.5">
                        {role.description}
                      </p>
                    )}

                    {/* Linked User Roles */}
                    {linkedRoles.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Link className="h-3.5 w-3.5 text-gray-400" />
                        <div className="flex flex-wrap gap-1">
                          {linkedRoles.map((ur) => (
                            <span
                              key={ur.id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700"
                            >
                              <Users className="h-3 w-3" />
                              {ur.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Checklist summary */}
                    {role.checklist && (
                      <p className="text-xs text-gray-400 mt-1">
                        {Array.isArray(role.checklist)
                          ? role.checklist.length
                          : JSON.parse(role.checklist as string).length}{" "}
                        checklist items
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(role)}
                    className={`px-3 py-1 text-xs rounded-full ${
                      role.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {role.is_active ? "Active" : "Inactive"}
                  </button>
                  <button
                    onClick={() => handleEdit(role)}
                    className="p-2 text-gray-400 hover:text-blue-600"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(role)}
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {approvalRoles.length === 0 && (
          <div className="card text-center py-8">
            <UserCheck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No approval roles defined yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Create approval roles to define who can approve deployments
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
